<template>
  <div class="mb-10 2xs:mb-2 sm:mb-0 recipients-list">
    <aha-spinner class="py-20" v-if="isSharedUserLoading" />

    <p class="py-20 text-center" v-else-if="hasErrorOnLoadingSharedUser">
      Cannot retrieve your recipient list.
      <br />
      Please <a @click.stop="getSharedUsers()" class="cursor-pointer primary-color">
        click here
      </a> to try again
    </p>

    <template v-else>
      <header>
        <CheckboxCopySlideNote :presentation="presentation" />
        <aha-checkbox v-model="isResponseDataIncluding">
          <span class="mr-1">Include response data</span>
          <aha-icon
            name="question"
            viewBox="0 0 24 24"
            width="16"
            height="16"
            class="text-secondary-20 mt-0.25"
            v-tooltip="TOOLTIP_INCLUDE_RESPONSE"
          />
        </aha-checkbox>
      </header>

      <hr class="my-3 text-base-30">

      <main
        class="overflow-auto"
        :class="{'max-h-72': $route.name === 'Presentations',
                 'max-h-72 sm:max-h-96': $route.name === 'PresentationDetail'}"
      >
        <table class="table-recipients">
          <thead>
            <tr>
              <th class="w-7">
                <aha-checkbox :checked="isAllSharedUsersSelected" @change="handleCheckAll" />
              </th>
              <th class="w-32">
                Recipient
              </th>
              <th class="text-left">
                Email
              </th>
              <th class="w-10" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(sharedUser, index) in sharedUsers"
              :key="`sharedUser-${sharedUser.id}`"
            >
              <td>
                <aha-checkbox
                  :checked="sharedUser.selected"
                  @change="toggleSelectRecipient(index)"
                />
              </td>
              <td class="truncate text-base-90">
                <span class="text-base-70">{{ index + 1 }}.</span> {{ sharedUser.name }}
              </td>
              <td class="truncate text-base-80">
                {{ sharedUser.email }}
              </td>
              <td>
                <aha-icon
                  v-if="sharedUser.lastSent"
                  class="text-secondary-20 hover:text-secondary-40"
                  name="circle-check"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  v-tooltip="'Last sent ' + $options.filters.dateTimeAgo(sharedUser.lastSent)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </main>
      <hr class="my-3 text-base-30" />
      <footer class="relative h-10 mb-3">
        <p v-if="numberOfSelectedSharedUsers > 0" class="absolute left-0 py-2">
          <span class="mr-0.5">
            {{ numberOfSelectedSharedUsers }}
            selected
            {{ numberOfSelectedSharedUsers === 1 ? 'recipient' : 'recipients' }}
          </span>
          <a @click="deselectRecipients()" class="text-base-60 text-link">
            <aha-icon
              name="close"
              width="16"
              height="16"
              viewBox="0 0 24 24"
            />
          </a>
        </p>
        <aha-button
          @click="onClickSend"
          class="absolute right-0"
          :submitting="isPresentationSending"
          :disabled="numberOfSelectedSharedUsers === 0"
        >
          Send
        </aha-button>
      </footer>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import PresentationApi from '@/api/presentation'
import UserApi from '@/api/user'
import CheckboxCopySlideNote from '@/components-v2/presentation-editor/collaboration/CheckboxCopySlideNote.vue'

// utils
import utils from '@/util'

import constants from '@/constant'

const { gaCategories } = constants
const FIVE_MINUTES = 300000

export default {
  components: {
    CheckboxCopySlideNote,
  },
  props: {
    presentation: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isResponseDataIncluding: false,
      sharedUsers: [],
      isPresentationSending: false,
      isSharedUserLoading: false,
      hasErrorOnLoadingSharedUser: false,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
    }),
    isAllSharedUsersSelected() {
      return this.sharedUsers.every((user) => user.selected)
    },
    numberOfSelectedSharedUsers() {
      return this.sharedUsers.filter((user) => user.selected).length
    },
  },
  created() {
    this.TOOLTIP_INCLUDE_RESPONSE = 'Response data is anything submitted by your audience (answers, reactions, Q&A questions, etc.)'
  },
  async mounted() {
    this.getSharedUsers()
  },
  methods: {
    async getSharedUsers() {
      this.isSharedUserLoading = true
      this.hasErrorOnLoadingSharedUser = false

      try {
        if (this.presentation.userId === this.currentUser.id) {
          const sharedUsers = await PresentationApi.getSharedUsers(this.presentation.id)
          this.sharedUsers = sharedUsers.filter((user) => user.email !== this.currentUser.email)
        } else {
          const res = await UserApi.getTeamMembers({
            userId: this.currentUser.id,
            page: 1,
            size: 500,
          })
          this.sharedUsers = res.users.map((user) => {
            return {
              name: `${user.firstName} ${user.lastName}`,
              email: user.email,
              id: user.id,
            }
          }).filter((user) => user.email !== this.currentUser.email)
        }

        this.handleLocalSendPresentationData()
      } catch (error) {
        this.isSharedUserLoading = false
        this.hasErrorOnLoadingSharedUser = true
        throw error
      }
      this.isSharedUserLoading = false
    },
    toggleSelectRecipient(index) {
      const newList = [...this.sharedUsers]
      newList[index].selected = !newList[index].selected
      this.sharedUsers = newList
    },
    handleCheckAll() {
      this.sharedUsers = this.sharedUsers.map((sharedUser) => ({
        ...sharedUser,
        selected: !this.isAllSharedUsersSelected,
      }))
    },
    async onClickSend() {
      let gaCategoryType = gaCategories.PRESENTATION_EDITOR
      if (this.$route.name === 'Presentations' || this.$route.name === 'FolderDetail') {
        gaCategoryType = gaCategories.PRESENTATION_LIST
      }
      utils.gaTrackEvent(gaCategoryType, 'Presentation > Send > Submit')
      const userIds = this.sharedUsers.filter((user) => user.selected).map((user) => user.id)
      if (!userIds.length || this.isPresentationSending) return
      this.isPresentationSending = true
      try {
        const params = {
          userIds,
          isResponseDataIncluding: this.isResponseDataIncluding,
        }

        await PresentationApi.sendPresentationCopy(this.presentation.id, params)
        utils.gaTrackEvent(gaCategoryType, 'Presentation > Send > Succeeded')
        utils.showToastSuccess(`Sent presentation to ${userIds.length} recipient(s) successfully`)

        window.localStorage.setItem(`send-presentation-${this.presentation.id}`, JSON.stringify({
          time: new Date(),
          timeEnd: new Date().getTime() + FIVE_MINUTES,
          users: userIds,
        }))
        this.deselectRecipients()
        this.handleLocalSendPresentationData()
      } catch {
        utils.gaTrackEvent(gaCategoryType, 'Presentation > Send > Failed')
        utils.showToastError(`Sent presentation to ${userIds.length} recipient(s) unsuccessfully`)
      }

      this.isPresentationSending = false
    },
    deselectRecipients() {
      this.sharedUsers = this.sharedUsers.map((sharedUser) => ({
        ...sharedUser,
        selected: false,
      }))
    },

    handleLocalSendPresentationData() {
      const data = window.localStorage.getItem(`send-presentation-${this.presentation.id}`)
      if (!data) return
      const receivedUsersData = JSON.parse(data)
      if (receivedUsersData.timeEnd < new Date().getTime()) {
        window.localStorage.removeItem(`send-presentation-${this.presentation.id}`)
        return
      }
      try {
        const arr = this.sharedUsers.map((item) => {
          const index = receivedUsersData.users.findIndex((id) => {
            return id === item.id
          })
          if (index !== -1) {
            item.lastSent = receivedUsersData.time
          }
          return item
        })
        this.sharedUsers = arr
      } catch {
        // todo nothing
      }
    },
  },
}
</script>

<style lang="scss" scoped>
  .table-recipients {
    @apply border-collapse table-fixed w-full;

    th, td {
      @apply p-2;
    }

    th:nth-child(2), td:nth-child(2) {
      @apply pl-0 text-left;
    }

    tbody > tr:nth-child(odd) {
      @apply bg-secondary-10;

      > td:first-child {
        @apply rounded-l-sm;
      }

      > td:last-child {
        @apply rounded-r-sm;
      }
    }
  }
</style>
